import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  isNotificationSupported,
  getNotificationPermission,
  areNotificationsEnabled,
  requestNotificationPermission,
  enableNotifications,
  disableNotifications,
  showOrderStatusNotification,
  playNotificationSound
} from '../services/notificationService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useNotifications() {
  const { token, user } = useAuth();
  const [permission, setPermission] = useState(getNotificationPermission());
  const [enabled, setEnabled] = useState(areNotificationsEnabled());
  const [supported] = useState(isNotificationSupported());
  const previousOrdersRef = useRef({});

  // Update permission state
  useEffect(() => {
    setPermission(getNotificationPermission());
    setEnabled(areNotificationsEnabled());
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(getNotificationPermission());
    setEnabled(areNotificationsEnabled());
    return result;
  }, []);

  // Toggle notifications
  const toggleNotifications = useCallback((value) => {
    if (value) {
      const success = enableNotifications();
      setEnabled(success);
      return success;
    } else {
      disableNotifications();
      setEnabled(false);
      return true;
    }
  }, []);

  // Check for order status changes and show notifications
  const checkOrderUpdates = useCallback(async () => {
    if (!token || !user || user.role !== 'Client' || !enabled) return;

    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const orders = response.data;
      
      // Check each order for status changes
      orders.forEach(order => {
        const previousStatus = previousOrdersRef.current[order.id];
        
        // If we have a previous status and it changed
        if (previousStatus && previousStatus !== order.status) {
          // Show notification for status change
          showOrderStatusNotification(order, order.status, () => {
            // Navigate to order detail on click
            window.location.href = `/commandes/${order.id}`;
          });
          playNotificationSound();
        }
        
        // Update previous status
        previousOrdersRef.current[order.id] = order.status;
      });
    } catch (error) {
      console.error('Error checking order updates:', error);
    }
  }, [token, user, enabled]);

  // Poll for updates every 15 seconds
  useEffect(() => {
    if (!enabled || !token || !user || user.role !== 'Client') return;

    // Initial check
    checkOrderUpdates();

    // Set up polling
    const interval = setInterval(checkOrderUpdates, 15000);

    return () => clearInterval(interval);
  }, [enabled, token, user, checkOrderUpdates]);

  return {
    supported,
    permission,
    enabled,
    requestPermission,
    toggleNotifications
  };
}
